import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Microphone, Play, Stop, CheckCircle, Warning, ArrowClockwise } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { adminApi, getErrorMessage } from "@/lib/api";
import { VoiceFilterBar, useVoiceOptions, DEFAULT_VOICE_FILTERS } from "@/components/admin/VoiceFilters";

function VoiceRow({ v, selected, playing, onPreview, onSelect, paidPlan }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 border-b border-neutral-800/60 ${selected ? "bg-[#73bf44]/[0.06]" : ""}`} data-testid="voice-option-row">
      <button onClick={() => onPreview(v)} disabled={!v.preview_url} data-testid="voice-preview-btn" className="w-8 h-8 rounded-full border border-neutral-700 flex items-center justify-center text-neutral-300 hover:border-[#73bf44] hover:text-[#73bf44] disabled:opacity-30 shrink-0">
        {playing ? <Stop size={14} weight="fill" /> : <Play size={14} weight="fill" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate flex items-center gap-2">
          {v.name}
          {v.accent === "australian" && <span className="text-[0.6rem] uppercase tracking-wide text-[#73bf44]">AU</span>}
          {v.needs_paid_plan && paidPlan && <span className="text-[0.6rem] uppercase tracking-wide text-[#73bf44] border border-[#73bf44]/40 rounded px-1" data-testid="voice-badge-included">Included in your plan</span>}
          {v.needs_paid_plan && !paidPlan && <span className="text-[0.6rem] uppercase tracking-wide text-amber-400 border border-amber-500/40 rounded px-1" data-testid="voice-badge-paid">Paid plan</span>}
          {v.needs_paid_plan ? null
            : <span className="text-[0.6rem] uppercase tracking-wide text-neutral-400 border border-neutral-600 rounded px-1">Built-in</span>}
        </div>
        <div className="text-xs text-neutral-500 truncate">{[v.age && v.age.replace("_", " "), v.use_case && v.use_case.replace(/_/g, " "), v.description].filter(Boolean).join(" · ")}</div>
      </div>
      {selected ? (
        <span className="text-xs text-[#73bf44] flex items-center gap-1 shrink-0"><CheckCircle size={14} weight="fill" /> Nick's voice</span>
      ) : (
        <button onClick={() => onSelect(v)} data-testid="voice-select-btn" className="text-xs text-neutral-300 border border-neutral-700 rounded-full px-3 py-1 hover:border-[#73bf44] hover:text-white shrink-0">Use</button>
      )}
    </div>
  );
}

function BackupWarning({ cfg, onRetry }) {
  const since = cfg.backup_since ? new Date(cfg.backup_since).toLocaleString() : "recently";
  return (
    <div className="flex gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4" data-testid="voice-backup-warning">
      <Warning size={16} className="shrink-0" />
      <div className="flex-1">
        <div className="font-semibold text-red-200 mb-0.5">Backup voice in use</div>
        ElevenLabs {cfg.backup_reason || "was unavailable"} (since {since}), so customers are hearing their own phone or computer's
        built-in voice. Nick switches back to ElevenLabs automatically once it's working again, e.g. after you top up credits.
      </div>
      <button onClick={onRetry} data-testid="voice-backup-retry-btn" className="self-start shrink-0 text-xs text-white border border-red-400/50 rounded-full px-3 py-1 hover:bg-red-500/20">
        Try ElevenLabs again
      </button>
    </div>
  );
}

function Usage({ cfg, onRecheck, checking }) {
  const pct = cfg.character_limit == null ? 0 : Math.min(100, Math.round((cfg.characters_used / Math.max(1, cfg.character_limit)) * 100));
  return (
    <div className="mb-4" data-testid="voice-usage">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500 mb-1">
        <span>ElevenLabs plan: <span className="text-white capitalize" data-testid="voice-plan-name">{cfg.plan || "unknown"}</span></span>
        <div className="flex items-center gap-3">
          {cfg.character_limit != null && <span>{cfg.characters_used?.toLocaleString()} / {cfg.character_limit?.toLocaleString()} characters this month</span>}
          <button onClick={onRecheck} disabled={checking} data-testid="voice-recheck-plan-btn" className="inline-flex items-center gap-1 text-[#73bf44] hover:text-white disabled:opacity-50">
            <ArrowClockwise size={12} className={checking ? "animate-spin" : ""} /> {checking ? "Checking…" : "Re-check plan"}
          </button>
        </div>
      </div>
      {cfg.plan_error && <div className="text-xs text-red-300 mb-1" data-testid="voice-plan-error">{cfg.plan_error} — check the ELEVENLABS_API_KEY secret is the key from your upgraded account.</div>}
      {cfg.character_limit != null && <div className="h-1.5 rounded-full bg-neutral-800 overflow-hidden"><div className="h-full bg-[#73bf44]" style={{ width: `${pct}%` }} /></div>}
    </div>
  );
}

export function VoiceSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_VOICE_FILTERS);
  const options = useVoiceOptions(filters);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    adminApi.get("/admin/voice-settings").then(({ data }) => setCfg(data)).catch(() => toast.error("Couldn't load voice settings"));
    return () => audioRef.current?.pause();
  }, []);

  const save = async (patch) => {
    try {
      const { data } = await adminApi.put("/admin/voice-settings", { enabled: cfg.enabled, ...patch });
      setCfg(data);
      toast.success("Voice settings saved");
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const preview = (v) => {
    audioRef.current?.pause();
    if (playing === v.voice_id) { setPlaying(null); return; }
    const a = new Audio(v.preview_url);
    a.onended = () => setPlaying(null);
    a.play().catch(() => toast.error("Couldn't play the preview"));
    audioRef.current = a;
    setPlaying(v.voice_id);
  };

  const [checking, setChecking] = useState(false);
  const retry = async (msg = "Nick will try ElevenLabs on the next reply") => {
    setChecking(true);
    try {
      const { data } = await adminApi.post("/admin/voice-settings/retry");
      setCfg(data);
      toast.success(typeof msg === "string" ? msg : "Nick will try ElevenLabs on the next reply");
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setChecking(false); }
  };
  const recheck = () => retry("Plan re-checked — Nick will use your chosen voice on the next reply");

  const select = (v) => save({ voice_id: v.voice_id, voice_name: v.name, public_owner_id: v.public_owner_id });

  return (
    <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-6" data-testid="voice-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <Microphone size={20} className="text-[#73bf44]" weight="duotone" />
        <h2 className="font-heading font-semibold text-white">Voice Chat · ElevenLabs</h2>
      </div>
      <p className="text-sm text-neutral-500 mb-4">Customers tap “Talk” to chat with Nick hands-free. He listens, answers out loud, then listens again.</p>
      {!cfg ? <div className="text-neutral-600 text-sm">Loading...</div> : (
        <>
          <div className="flex items-center justify-between py-3 border-b border-neutral-800/60 mb-4">
            <div>
              <div className="text-sm text-white">Show the Talk button to customers</div>
              <div className="text-xs text-neutral-500 mt-0.5">Nick's voice: <span className="text-neutral-300" data-testid="voice-current-name">{cfg.voice_name}</span></div>
            </div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="voice-enabled-toggle" />
          </div>
          {cfg.backup_active && <BackupWarning cfg={cfg} onRetry={retry} />}
          {cfg.fallback_active && !cfg.backup_active && (
            <div className="flex gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4" data-testid="voice-fallback-warning">
              <Warning size={16} className="shrink-0" />
              <span>Your ElevenLabs plan can't use “{cfg.voice_name}” yet, so Nick is talking as {cfg.fallback_voice?.voice_name} for now. Upgrade to a paid ElevenLabs plan (Starter or above) and he'll switch automatically.</span>
            </div>
          )}
          <Usage cfg={cfg} onRecheck={recheck} checking={checking} />
          <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Choose Nick's voice · most popular first</div>
          {cfg.plan === "free" && (
            <div className="text-xs text-neutral-400 mb-3" data-testid="voice-plan-note">
              On the free ElevenLabs plan Nick can only use <span className="text-neutral-200">Built-in</span> voices. <span className="text-amber-300">Paid plan</span> voices work once you upgrade to Starter or above. Preview them any time.
            </div>
          )}
          <VoiceFilterBar filters={filters} onChange={setFilters} />
          <div className="border border-neutral-800 rounded-lg max-h-96 overflow-y-auto" data-testid="voice-options-list">
            {options.error && <div className="p-3 text-sm text-red-300" data-testid="voice-options-error">{options.error}</div>}
            {options.rows?.length === 0 && !options.loading && !options.error && <div className="p-3 text-sm text-neutral-500" data-testid="voice-options-empty">No voices match those filters.</div>}
            {(options.rows || []).map((v) => (
              <VoiceRow key={v.voice_id} v={v} paidPlan={!!cfg.plan && cfg.plan !== "free"} selected={v.voice_id === cfg.voice_id} playing={playing === v.voice_id} onPreview={preview} onSelect={select} />
            ))}
            {options.loading && <div className="p-3 text-sm text-neutral-600">Loading voices…</div>}
            {options.hasMore && !options.loading && (
              <button onClick={options.loadMore} data-testid="voice-load-more" className="w-full py-2.5 text-xs text-[#73bf44] hover:bg-[#73bf44]/5">Load more voices</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
